import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { UPLOAD_CONFIG, UPLOAD_ENDPOINTS, ERROR_MESSAGES, METADATA_FILES, S3_CONFIG } from '../constants/upload.constants';
import { SessionMetadataHelper } from '../utils/sessionMetadataHelper';
// Worker import type hint (actual new Worker below)
// @ts-ignore
import BlurWorker from '../workers/blurWorker?worker';
import { s3CredentialsManager } from './s3CredentialsManager.service';

import { getFileType, supportsBlurDetection } from '../utils/fileTypeDetection';


import { 
  CredentialsResponse, 
  UploadProgress, 
  UploadResult, 
  UploadMetadata, 
  FolderStructure, 
  UploadStatus 
} from '../types/upload.types';
import { ImageFile, Scene } from '../types/core.types';
import { customerApi } from './customerApi';

// Thumbnail utilities
import { generateThumbnail } from '../utils/thumbnail';



/**
 * Get the S3 folder name for a given camera ID
 * Supports dynamic camera count (1-20)
 * @param cameraId - The camera ID from the frontend (camera1, camera2, ..., camera20)
 * @returns The folder name for S3 storage (same as cameraId)
 */
function getCameraFolderName(cameraId: string | undefined): string {
  if (!cameraId || cameraId === 'unknown') {
    return 'camera1'; // Default to camera1
  }
  
  // Validate format: camera1, camera2, ..., camera20
  if (!/^camera\d+$/.test(cameraId)) {
    console.warn(`⚠️ Invalid camera ID format: ${cameraId}, defaulting to camera1`);
    return 'camera1';
  }
  
  // Simply return the cameraId - no mapping needed
  return cameraId;
}

export class S3UploadService {
  private s3Client: S3Client | null = null;
  private abortController: AbortController | null = null;
  private sessionMetadataHelper: SessionMetadataHelper | null = null;
  // Whether thumbnails for this upload session should be rotated 90° left (smartphone camera)
  private rotateLeftForThumbnail = false;
  private credentialsExpirationTime: Date | null = null;
  private currentCredentials: CredentialsResponse | null = null;
  private currentFolderStructure: FolderStructure | null = null;


  /**
   * Check if credentials need refresh (with 10-minute buffer)
   */
  private needsCredentialsRefresh(): boolean {
    if (!this.credentialsExpirationTime) return true;
    
    const now = new Date();
    const refreshThreshold = new Date(this.credentialsExpirationTime.getTime() - 10 * 60 * 1000); // 10 min buffer
    
    return now >= refreshThreshold;
  }

  /**
   * Refresh S3 credentials and client
   */
  private async refreshCredentials(): Promise<void> {
    if (!this.currentFolderStructure) {
      throw new Error('Cannot refresh credentials without folder structure');
    }

    console.log('🔄 Refreshing S3 credentials...');
    
    try {
      const newCredentials = await s3CredentialsManager.getUploadCredentials(
        this.currentFolderStructure.fullPath, 
        100 // Approximate remaining files
      );

      if (!newCredentials.success || !newCredentials.data) {
        throw new Error('Failed to refresh upload credentials');
      }

      // Convert to expected format
      this.currentCredentials = {
        credentials: newCredentials.data.credentials,
        uploadId: newCredentials.data.uploadId,
        bucket: newCredentials.data.bucket,
        region: newCredentials.data.region,
        prefix: newCredentials.data.prefix
      };

      // Update expiration time
      this.credentialsExpirationTime = new Date(newCredentials.data.credentials.Expiration);

      // Reinitialize S3 client with new credentials
      this.s3Client = new S3Client({
        region: this.currentCredentials.region,
        credentials: {
          accessKeyId: this.currentCredentials.credentials.AccessKeyId,
          secretAccessKey: this.currentCredentials.credentials.SecretAccessKey,
          sessionToken: this.currentCredentials.credentials.SessionToken
        }
      });

      console.log('✅ S3 credentials refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh credentials:', error);
      throw new Error('Credential refresh failed - upload cannot continue');
    }
  }


  /**
   * Convert timestamp to IST and format for use in filenames/folders
   */
  private toISTTimestamp(timestamp: number): string {
    // IST is UTC+5:30 (5.5 hours = 19800000 milliseconds)
    const istTimestamp = timestamp + (5.5 * 60 * 60 * 1000);
    const date = new Date(istTimestamp);
    
    // Format as YYYY-MM-DD_HH-mm-ss in IST
    return date.toISOString()
      .replace(/T/, '_')
      .replace(/:/g, '-')
      .slice(0, 19); // Remove milliseconds and timezone
  }

  /**
   * Generate folder- & path information for an upload session.
   *
   * If a `forcedUuid` is supplied we reuse that value so the web-app can
   * pre-mint the sequence UUID (right after the scan-metadata form) and ensure
   * that *all* subsequent artefacts – Scan, ScanVersion, S3 folders, etc. –
   * share the same identifier.
   */
  generateFolderStructure(userId: string,forcedUuid?: string): FolderStructure {
    
    const sessionUUID = forcedUuid || crypto.randomUUID();
    const baseTimestamp = Date.now();
    
    // Format timestamp for folder name using IST
    const timestamp = this.toISTTimestamp(baseTimestamp);
    
    // Create final folder name directly (no draft suffix)
    const folderName = `${S3_CONFIG.folderPrefix}_${sessionUUID}_${timestamp}`;
    const fullPath = `${userId}/${folderName}`;

    console.log(`📁 Generated folder structure with IST timestamp: ${timestamp}`);

    return {
      userId,
      folderName,
      fullPath,
      sessionUUID,
      baseTimestamp
    };
  }

  /**
   * Get S3 credentials from backend with retry logic
   */
  
async getCredentials(folderPath: string, fileCount: number, retryCount = 0): Promise<CredentialsResponse> {
  try {
    console.log(`[S3UploadService] 📤 Getting upload credentials via session manager for: ${folderPath}`);
    
    // Use the centralized credentials manager
    const response = await s3CredentialsManager.getUploadCredentials(folderPath, fileCount);
    
    if (!response.success || !response.data) {
      throw new Error('Failed to get upload credentials from session manager');
    }

    console.log(`[S3UploadService] ✅ Upload credentials obtained via session manager`);
    
    // Convert to the expected format
    return {
      credentials: response.data.credentials,
      uploadId: response.data.uploadId,
      bucket: response.data.bucket,
      region: response.data.region,
      prefix: response.data.prefix
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Credentials request timed out');
      }
      throw error;
    }
    throw new Error('Unknown error getting credentials');
  }
}

  /**
   * Initialize S3 client and session metadata helper with progress tracking
   */
  initializeS3Client(
    credentials: CredentialsResponse, 
    folderStructure: FolderStructure, 
    scene: Scene,
    onProgress?: (uploadedCount: number, currentFile: string, stage?: string) => void
  ): void {
    try {
      this.s3Client = new S3Client({
        region: credentials.region,
        credentials: {
          accessKeyId: credentials.credentials.AccessKeyId,
          secretAccessKey: credentials.credentials.SecretAccessKey,
          sessionToken: credentials.credentials.SessionToken
        }
      });

      // Initialize session metadata helper with progress callback
      const progressCallback = (step: string, progress: number, currentItem?: string) => {
        console.log(`[SessionMetadata] ${step} (${progress}%)${currentItem ? ` - ${currentItem}` : ''}`);
        // Route blur-related progress to the hook with a special stage identifier so the UI can distinguish it
        const lowerStep = step.toLowerCase();
        if (lowerStep.includes('blur')) {
          onProgress?.(progress, currentItem || step, 'blur-detection');
        }
      };

      this.sessionMetadataHelper = new SessionMetadataHelper(
        folderStructure.sessionUUID,
        folderStructure,
        scene,
        progressCallback
      );

      // Determine if thumbnails need rotation based on camera type
      this.rotateLeftForThumbnail = scene.cameraType === 'smartphone';
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to initialize S3 client: ${errorMessage}`);
    }
  }

  /**
   * Create upload metadata for S3 object
   */
  private createUploadMetadata(
    file: ImageFile, 
    uploadId: string,
    orderedFilename: string,
    sequenceNumber: number,
    sessionUUID: string,
    validationResults?: any[]
  ): UploadMetadata {
    return {
      originalName: file.file.name,
      cameraId: file.cameraId || 'unknown',
      uploadSession: uploadId,
      fileSize: file.file.size.toString(),
      uploadTimestamp: Date.now().toString(),
      orderedFilename,
      sequenceNumber: sequenceNumber.toString(),
      sessionUUID,
      ...(validationResults && { validationResults })
    };
  }

  /**
   * Convert File to Uint8Array for reliable S3 upload
   */
  private async fileToUint8Array(file: File | Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(reader.result));
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file as ArrayBuffer'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Generate a JPEG thumbnail (~400 px wide) for the first image and upload as thumb_<timestamp>.jpg
   */
  private async uploadThumbnail(
    image: ImageFile,
    credentials: CredentialsResponse,
    orderedFilename: string,
    folderStructure: FolderStructure
  ): Promise<void> {
    try {
      // Generate thumbnail, rotating left for smartphone data sets
      const thumbBlob = await generateThumbnail(
        image.file,
        400,
        0.7,
        this.rotateLeftForThumbnail
      );

      const thumbArray = await this.fileToUint8Array(thumbBlob);

      const thumbFilename = 'thumb.jpg';
      const key = `${credentials.prefix}${thumbFilename}`;

      const command = new PutObjectCommand({
        Bucket: credentials.bucket,
        Key: key,
        Body: thumbArray,
        ContentType: 'image/jpeg',
        Metadata: {
          'thumbnail': 'true',
          'session-uuid': folderStructure.sessionUUID,
          'source-photo': orderedFilename
        }
      });

      console.log('Uploading thumbnail', thumbFilename);
      await this.s3Client!.send(command, {
        abortSignal: this.abortController?.signal
      });
      console.log('✅ Thumbnail uploaded');
    } catch (err) {
      console.warn('uploadThumbnail failed:', err);
    }
  }

  /**
   * Upload single file to S3 with sequential naming
   */
  async uploadFile(
    file: ImageFile,
    credentials: CredentialsResponse,
    sequenceNumber: number,
    folderStructure: FolderStructure,
    validationResults?: any[],
    retryCount = 0
  ): Promise<string> {

    // Check if credentials need refresh before upload
    if (this.needsCredentialsRefresh()) {
      await this.refreshCredentials();
      // Use refreshed credentials for this upload
      credentials = this.currentCredentials!;
    }

    // Store current context for potential refreshes
    this.currentCredentials = credentials;
    this.currentFolderStructure = folderStructure;
    if (!this.credentialsExpirationTime) {
      this.credentialsExpirationTime = new Date(credentials.credentials.Expiration);
    }

    if (!this.s3Client) {
      throw new Error(ERROR_MESSAGES.S3_CLIENT_NOT_INITIALIZED);
    }

    // Generate ordered filename
    const orderedFilename = SessionMetadataHelper.generateOrderedFilename(
      file.file.name,
      folderStructure.baseTimestamp,
      sequenceNumber
    );

    // Get camera folder for multi-camera support
    const cameraFolder = getCameraFolderName(file.cameraId);
    //add applicable camera folder in the key
    const key = `${credentials.prefix}${cameraFolder}/${orderedFilename}`;
    
    console.log(`📸 Camera folder: ${file.cameraId} → ${cameraFolder}`);
    const metadata = this.createUploadMetadata(
      file, 
      credentials.uploadId, 
      orderedFilename,
      sequenceNumber,
      folderStructure.sessionUUID,
      validationResults
    );
    
    try {
      console.log(`Converting ${file.file.name} to ${orderedFilename}...`);
      const fileBuffer = await this.fileToUint8Array(file.file);
      console.log(`✅ Successfully converted ${orderedFilename} (${fileBuffer.byteLength} bytes)`);
      
      const command = new PutObjectCommand({
        Bucket: credentials.bucket,
        Key: key,
        Body: fileBuffer,
        ContentType: file.file.type || 'application/octet-stream',
        Metadata: {
          'original-name': metadata.originalName,
          'camera-id': metadata.cameraId,
          'upload-session': metadata.uploadSession,
          'file-size': metadata.fileSize,
          'upload-timestamp': metadata.uploadTimestamp,
          'ordered-filename': metadata.orderedFilename,
          'sequence-number': metadata.sequenceNumber,
          'session-uuid': metadata.sessionUUID
        }
      });

      console.log(`Uploading ${orderedFilename}...`);
      await this.s3Client.send(command, {
        abortSignal: this.abortController?.signal
      });
      console.log(`✅ Successfully uploaded ${orderedFilename}`);

      // Add to session metadata with proper error handling
      if (this.sessionMetadataHelper) {
        try {
          // Include camera folder in the path for metadata
          const imagePathWithCamera = `${cameraFolder}/${orderedFilename}`;
          const result = await this.sessionMetadataHelper.addImage(file, 
            sequenceNumber, 
            imagePathWithCamera,  // ✅ Now includes camera1/, camera2/, etc.
            null,                 // providedDimensions
            file.cameraId         // ✅ Pass cameraId for multi-camera support
          );
          if (result.error) {
            console.warn(`Metadata generation warning for ${orderedFilename}: ${result.error}`);
          }
        } catch (metadataError) {
          console.error(`Failed to add image to session metadata for ${orderedFilename}:`, metadataError);
          // Continue upload despite metadata error - don't fail the entire upload
        }
      }
      
      // Upload a downscaled thumbnail for the very first image so dashboards load fast
      if (sequenceNumber === 0 && this.s3Client) {
        try {
          await this.uploadThumbnail(file, credentials, orderedFilename, folderStructure);
        } catch (thumbErr) {
          console.warn('Failed to generate/upload thumbnail:', thumbErr);
        }
      }
      
      return orderedFilename;
      
    } catch (error: unknown) {
      // Check if it's a credentials error and retry with refresh
      if (error instanceof Error && error.message.includes('expired') && retryCount === 0) {
        console.log('🔄 Credentials expired during upload, refreshing and retrying...');
        await this.refreshCredentials();
        return this.uploadFile(file, this.currentCredentials!, sequenceNumber, folderStructure, validationResults, retryCount + 1);
      }
      if (this.abortController?.signal.aborted) {
        throw new Error(ERROR_MESSAGES.UPLOAD_CANCELLED);
      }
      
      if (retryCount < UPLOAD_CONFIG.maxRetries) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn(`Upload failed for ${orderedFilename}, retrying... (${retryCount + 1}/${UPLOAD_CONFIG.maxRetries}): ${errorMessage}`);
        
        await new Promise(resolve => setTimeout(resolve, UPLOAD_CONFIG.retryDelay * (retryCount + 1)));
        return this.uploadFile(file, credentials, sequenceNumber, folderStructure, validationResults, retryCount + 1);
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';
      throw new Error(`Failed to upload ${orderedFilename}: ${errorMessage}`);
    }
  }

  /**
   * Upload metadata files (sessionMetadata.json and scanMetadata.json) with enhanced error handling
   */
  async uploadMetadataFiles(credentials: CredentialsResponse): Promise<string[]> {
    if (!this.s3Client || !this.sessionMetadataHelper) {
      throw new Error(ERROR_MESSAGES.S3_CLIENT_NOT_INITIALIZED);
    }

    const uploadedMetadataFiles: string[] = [];

    try {
      // Validate metadata before upload
      const validation = this.sessionMetadataHelper.validateMetadata();
      if (!validation.isValid) {
        throw new Error(`Metadata validation failed: ${validation.errors.join(', ')}`);
      }

      // Upload sessionMetadata.json
      let sessionMetadataJSON: string;
      try {
        sessionMetadataJSON = this.sessionMetadataHelper.generateSessionMetadataJSON();
      } catch (error) {
        throw new Error(`Failed to generate session metadata: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      const sessionMetadataKey = `${credentials.prefix}${METADATA_FILES.SESSION_METADATA}`;
      
      const sessionMetadataCommand = new PutObjectCommand({
        Bucket: credentials.bucket,
        Key: sessionMetadataKey,
        Body: new TextEncoder().encode(sessionMetadataJSON),
        ContentType: 'application/json',
        Metadata: {
          'upload-session': credentials.uploadId,
          'session-uuid': this.sessionMetadataHelper.getSessionUUID(),
          'file-type': 'session-metadata'
        }
      });

      console.log('Uploading sessionMetadata.json...');
      await this.s3Client.send(sessionMetadataCommand, {
        abortSignal: this.abortController?.signal
      });
      console.log('✅ Successfully uploaded sessionMetadata.json');
      uploadedMetadataFiles.push(METADATA_FILES.SESSION_METADATA);

      // Upload scanMetadata.json
      let scanMetadataJSON: string;
      try {
        scanMetadataJSON = this.sessionMetadataHelper.generateScanMetadataJSON();
      } catch (error) {
        throw new Error(`Failed to generate scan metadata: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      const scanMetadataKey = `${credentials.prefix}${METADATA_FILES.SCAN_METADATA}`;
      
      const scanMetadataCommand = new PutObjectCommand({
        Bucket: credentials.bucket,
        Key: scanMetadataKey,
        Body: new TextEncoder().encode(scanMetadataJSON),
        ContentType: 'application/json',
        Metadata: {
          'upload-session': credentials.uploadId,
          'session-uuid': this.sessionMetadataHelper.getSessionUUID(),
          'file-type': 'scan-metadata'
        }
      });

      console.log('Uploading scanMetadata.json...');
      await this.s3Client.send(scanMetadataCommand, {
        abortSignal: this.abortController?.signal
      });
      console.log('✅ Successfully uploaded scanMetadata.json');
      uploadedMetadataFiles.push(METADATA_FILES.SCAN_METADATA);

    } catch (error: unknown) {
      if (this.abortController?.signal.aborted) {
        throw new Error(ERROR_MESSAGES.UPLOAD_CANCELLED);
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`${ERROR_MESSAGES.METADATA_GENERATION_FAILED}: ${errorMessage}`);
    }

    return uploadedMetadataFiles;
  }

  /**
   * Upload multiple files with concurrent processing for optimal performance
   */
  async uploadFiles(
    files: ImageFile[],
    credentials: CredentialsResponse,
    folderStructure: FolderStructure,
    scene: Scene,
    onProgress?: (uploadedCount: number, currentFile: string, stage?: string) => void,
    validationResults?: any[]
  ): Promise<UploadResult> {
    this.abortController = new AbortController();
    
    try {
      // Use configured concurrent uploads from constants
      const maxConcurrent = UPLOAD_CONFIG.concurrentUploads;      
      console.log(`📤 Starting concurrent upload of ${files.length} files (max ${maxConcurrent} concurrent)...`);
      
      // ===== CRITICAL: Set up credentials context BEFORE any uploads =====
      this.currentCredentials = credentials;
      this.currentFolderStructure = folderStructure;
      this.credentialsExpirationTime = new Date(credentials.credentials.Expiration);
      
      // Re-initialize SessionMetadataHelper with a progress callback that forwards updates to the UI
      this.sessionMetadataHelper = new SessionMetadataHelper(
        folderStructure.sessionUUID,
        folderStructure,
        scene,
        (step: string, progress: number, currentItem?: string) => {
          console.log(`🔄 Metadata: ${step} (${progress}%) ${currentItem || ''}`);
          const lowerStep = step.toLowerCase();
          if (lowerStep.includes('blur')) {
            onProgress?.(progress, currentItem || step, 'blur-detection');
          }
        }
      );
      
      console.log(`📊 Upload configuration:`, {
        bucket: credentials.bucket,
        region: credentials.region,
        prefix: credentials.prefix,
        totalFiles: files.length,
        folderPath: folderStructure.fullPath,
        concurrentUploads: maxConcurrent
      });

      const uploadedFiles: string[] = [];
      const failedFiles: Array<{ fileName: string; error: string }> = [];
      let completedCount = 0;

      // ===== BLUR DETECTION CHECK =====
      console.log('🟦 Starting blur detection worker...');
      // Check first file format to determine if blur detection should run
      const firstFileFormat = files.length > 0 ? getFileType(files[0].file) : 'unknown';
      const shouldRunBlurDetection = supportsBlurDetection(firstFileFormat);
      
      console.log(`🔍 Format check: ${firstFileFormat.toUpperCase()} - Blur detection: ${shouldRunBlurDetection ? 'ENABLED' : 'SKIPPED'}`);
      
      let blurStatisticsPromise: Promise<{ stats: any; blurryFiles: string[] }>;
      let worker: Worker | null = null;

      if (shouldRunBlurDetection) {
        // Start blur detection worker (overlapped)
        console.log('🟦 Starting blur detection worker...');
        worker = new BlurWorker();

        // Terminate blur detection worker on cancellation
        if (this.abortController) {
          this.abortController.signal.addEventListener('abort', () => {
            worker?.terminate();
            console.log('Blur detection worker terminated due to cancellation');
          });
        }

      const workerId = folderStructure.sessionUUID;
      // Prepare transferable list: cannot send File objects inside structured
      const workerPayload = files.map((file, idx) => ({
        sequence: idx,
        file: file.file,
        orderedName: SessionMetadataHelper.generateOrderedFilename(file.file.name, folderStructure.baseTimestamp, idx),
      }));

      worker.postMessage({ id: workerId, files: workerPayload });

      blurStatisticsPromise = new Promise((resolve) => {
          worker!.addEventListener('message', (e: MessageEvent<any>) => {
            const { id, type } = e.data;
            if (id !== workerId) return;
            if (type === 'progress') {
              const percent = e.data.percent as number;
              onProgress?.(percent, 'blur', 'blur-detection');
            }
            if (type === 'done') {
              const { statistics, blurryFiles } = e.data;
              resolve({ stats: statistics, blurryFiles });
            }
          });
        });
      } else {
        // Skip blur detection for unsupported formats
        console.log(`⏭️ Skipping blur detection for ${firstFileFormat.toUpperCase()} format`);
        blurStatisticsPromise = Promise.resolve({
          stats: {
            totalImages: files.length,
            processedImages: 0,
            skippedImages: files.length,
            blurryImages: 0,
            blurryPercentage: 0,
            skippedFormats: [firstFileFormat.toUpperCase()]
          },
          blurryFiles: []
        });
        
        // Report blur detection completion immediately
        onProgress?.(100, 'Blur detection skipped for EXR format', 'blur-detection');
      }

      // ===== CONCURRENT UPLOAD LOGIC =====
      
      // Create upload tasks with proper sequencing
      const uploadTasks = files.map((file, index) => ({
        file,
        sequenceNumber: index, // 0-based indexing like your original
        attempt: 0
      }));

      /**
       * Process a single upload task
       */
      const processUploadTask = async (task: typeof uploadTasks[0]): Promise<void> => {
        try {
          onProgress?.(completedCount, task.file.file.name, 'uploading');
          
          // Use current credentials (which may get refreshed automatically in uploadFile)
          const orderedFilename = await this.uploadFile(
            task.file,
            this.currentCredentials!, // Use instance variable
            task.sequenceNumber,
            folderStructure,
            validationResults
          );
          
          uploadedFiles[task.sequenceNumber] = orderedFilename; // Maintain order
          completedCount++;
          
          // Update progress after each successful upload
          onProgress?.(completedCount, `Uploaded ${orderedFilename}`, 'uploading');
          
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`Failed to upload ${task.file.file.name}:`, errorMessage);
          
          failedFiles.push({
            fileName: task.file.file.name,
            error: errorMessage
          });
        }
      };

      /**
       * Process uploads with controlled concurrency
       */
      const processConcurrentUploads = async (): Promise<void> => {
        const activeUploads = new Set<Promise<void>>();
        
        for (const task of uploadTasks) {
          // Check for cancellation
          if (this.abortController?.signal.aborted) {
            throw new Error(ERROR_MESSAGES.UPLOAD_CANCELLED);
          }

          // Wait if we've hit the concurrency limit
          if (activeUploads.size >= maxConcurrent) {
            await Promise.race(activeUploads);
          }

          // Start the upload
          const uploadPromise = processUploadTask(task).finally(() => {
            activeUploads.delete(uploadPromise);
          });
          
          activeUploads.add(uploadPromise);
        }

        // Wait for all remaining uploads to complete
        await Promise.all(activeUploads);
      };

      // Execute concurrent uploads
      await processConcurrentUploads();

      // Wait for blur worker if not finished yet
      const { stats: workerStats, blurryFiles } = await blurStatisticsPromise;

      // Store results in helper
      this.sessionMetadataHelper?.setBlurResults(workerStats, blurryFiles);

      // Terminate worker
      if (worker) {
        worker.terminate();
      }

      // ===== END CONCURRENT UPLOAD LOGIC =====
      
      // Filter out any undefined entries (failed uploads) and maintain order
      const successfulFiles = uploadedFiles.filter(filename => filename !== undefined);
      
      // Check if we have any successful uploads
      if (successfulFiles.length === 0) {
        throw new Error('All file uploads failed');
      }

      // Upload metadata files
      console.log('\n📋 Uploading metadata files...');
      try {
        const metadataFiles = await this.uploadMetadataFiles(this.currentCredentials!);
        console.log(`✅ Successfully uploaded ${metadataFiles.length} metadata files`);
      } catch (metadataError) {
        console.error('Failed to upload metadata files:', metadataError);
        // Add metadata upload failure to failed files but don't fail the entire upload
        failedFiles.push({
          fileName: 'metadata files',
          error: metadataError instanceof Error ? metadataError.message : 'Unknown metadata error'
        });
      }

      const result: UploadResult = {
        uploadId: this.currentCredentials!.uploadId,
        folderName: folderStructure.folderName,
        fullPath: folderStructure.fullPath,
        fileCount: successfulFiles.length,
        uploadedFiles: successfulFiles,
        sessionUUID: folderStructure.sessionUUID,
        credentials: this.currentCredentials!, // Store credentials for later use
        blurStatistics: this.sessionMetadataHelper?.getBlurStatistics(),
        ...(failedFiles.length > 0 && { failedFiles })
      };

      console.log(`\n🎉 Concurrent upload completed! ${successfulFiles.length}/${files.length} files uploaded`);
      if (failedFiles.length > 0) {
        console.warn(`⚠️ ${failedFiles.length} files failed to upload:`, failedFiles);
      }

      return result;

    } catch (error: unknown) {
      if (this.abortController?.signal.aborted) {
        throw new Error(ERROR_MESSAGES.UPLOAD_CANCELLED);
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Concurrent upload process failed:', errorMessage);
      throw new Error(`Upload failed: ${errorMessage}`);
    }
  }

  /**
   * Set abort controller for cancellation
   */
  setAbortController(controller: AbortController): void {
    this.abortController = controller;
  }

  /**
   * Cancel ongoing upload
   */
  cancelUpload(): void {
    if (this.abortController) {
      this.abortController.abort();
      console.log('Upload cancelled by user');
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.s3Client = null;
    this.abortController = null;
    this.sessionMetadataHelper = null;
    console.log('S3 upload service cleaned up');
  }

  /**
   * Get upload status from backend
   */
  async getUploadStatus(uploadId: string): Promise<any> {
    return customerApi.getUploadStatus(uploadId);
  }
}

// Export singleton instance
export const s3UploadService = new S3UploadService();
export default s3UploadService;